import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { UseDataProps } from './types';

import { DECLARATIONS } from '10.quickConnect.app/routes/paths';
import { useStore } from '30.quickConnect.Stores';
import { DeclarationViewer } from '90.quickConnect.Models/models';
import { errorHandler } from '80.quickConnect.Core/helpers';
import useOpenDraft from '10.quickConnect.app/components/shared/Dialogs/OuSwitchPrompt/useOpenDraft';

const useData = (drafts: DeclarationViewer[], onCloseDialog: () => void): UseDataProps => {
  // Tag
  const tag = '10.quickConnect.app/components/shared/Dialogs/DeclarationAlreadySavedDialog/hooks.ts';

  const { t } = useTranslation('forms');
  const navigate = useNavigate();

  const {
    DeclarationStore: { deleteDraftDeclarationAsync },
  } = useStore();

  const { openDraft, promptProps } = useOpenDraft();

  const [singleDraft] = drafts.length === 1 ? drafts : [];

  // Un seul brouillon : il est repris ; sinon, l'utilisateur le choisit dans la liste des brouillons
  const onContinueDeclaration = useCallback(async () => {
    onCloseDialog();

    if (singleDraft) await openDraft(singleDraft);
    else navigate(DECLARATIONS);
  }, [singleDraft, onCloseDialog, openDraft, navigate]);

  // Un brouillon ouvert dans un onglet n'est pas supprimé
  const onDeleteDeclaration = useCallback(async () => {
    onCloseDialog();
    if (!singleDraft) return;

    try {
      if (await deleteDraftDeclarationAsync(singleDraft.id))
        toast.success(t('qcapp_form_choice_dialog_success_one').toString());
      else toast.warn(t('qcapp_declaration_opened_elsewhere').toString());
    } catch (error: unknown) {
      errorHandler(tag, error, 'onDeleteDeclaration');
      toast.error(t('qcapp_form_choice_dialog_error').toString());
    }
  }, [singleDraft, onCloseDialog, deleteDraftDeclarationAsync, t, tag]);

  return {
    onContinueDeclaration,
    onDeleteDeclaration,
    hasSingleDraft: !!singleDraft,
    promptProps,
  };
};

export default useData;
